// sublayout dashbordu - menu w sidebarze
// @ts-nocheck

import React, { FC, useCallback, useEffect } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import { Grid } from '@mui/material';
import classnames from 'classnames';
import each from 'lodash/each';
import find from 'lodash/find';

import { useSelector } from 'store';
import { useAppNavigate } from 'hooks';
import { Container, Breadcrumbs } from 'components/controls';
import { DashboardMenu } from 'components/containers';

import styles from 'theme/components/layouts/DashboardSubLayout/SubLayoutDashboard.module.scss';

// typ danych wejściowych
interface IProps {
  urlPrefix: string;
}

interface IRoute {
  label: string;
  path: string;
  key: string | null;
}

export const userRolesAccessRoutes = (isAdminPanel?: boolean) => {
  // Używamy bezpośredniego importu z core, aby uniknąć rekurencji
  // Musimy użyć pełnej ścieżki względnej, aby ominąć webpack alias
  const coreModule = require('../../../../packages/core/src/components/layouts/DashboardSubLayout/SubLayoutDashboard');
  const coreRoutes = coreModule.userRolesAccessRoutes(isAdminPanel);

  // Funkcja do reorganizacji routes zgodnie z nową strukturą menu
  const reorganizeRoutes = (routes: any, role?: string) => {
    const newRoutes: any = { ...routes };

    // Zamówienia: Lista zamówień, Listy zakupowe (z carts), Listy dostaw (Miejsca dostaw)
    if (newRoutes.orders && newRoutes.carts) {
      const shoppingLists = newRoutes.carts.find((item: IRoute) => item.key === 'shopping-lists');
      if (shoppingLists) {
        newRoutes.orders = [
          ...newRoutes.orders,
          shoppingLists
        ];
        newRoutes.carts = newRoutes.carts.filter((item: IRoute) => item.key !== 'shopping-lists');
      }
      
      // Ukryj Listy dostaw z menu
      newRoutes.orders = newRoutes.orders.filter((order: IRoute) => order.path !== '/dashboard/deliveries');

      // Zmiana nazw
      newRoutes.orders = newRoutes.orders.map((order: IRoute) => {
        if (order.path === '/dashboard/orders') {
          return { ...order, label: 'Lista zamówień' };
        }
        return order;
      });
    }

    // Moje koszyki: Importuj koszyk (Lista koszyków ukryta)
    if (newRoutes.carts) {
      newRoutes.carts = newRoutes.carts
        .filter((cart: IRoute) => cart.path !== '/dashboard/carts')
        .map((cart: IRoute) => {
          if (cart.path === '/dashboard/carts') {
            return { ...cart, label: 'Lista koszyków' };
          }
          return cart;
        });
    }

    // Ukryj "Lista płatności" dla zwykłego użytkownika
    if (role === 'ROLE_USER') {
      newRoutes.payments = [];
    }

    // Oferty: tylko Dedykowane i Okresowe (bez Export danych)
    if (newRoutes.offers) {
      newRoutes.offers = newRoutes.offers.filter((item: IRoute) => item.key !== 'export');
      
      // Moje dane: Moje konto, Komunikaty, Export danych, Moje dokumenty, Regulamin, FAQ
      if (newRoutes.myData) {
        // Dodaj Komunikaty jeśli istnieje (sprawdź czy route jest dostępny)
        // Komunikaty mogą być dostępne przez /dashboard/consent lub osobny route
        // Jeśli nie ma osobnego route, pomijamy
        
        // Dodaj Moje dokumenty z documents (ukryte dla ROLE_USER)
        if (role !== 'ROLE_USER' && newRoutes.documents && newRoutes.documents.length > 0) {
          const myDocuments = newRoutes.documents.find((item: IRoute) => item.key === 'documents');
          if (myDocuments) {
            newRoutes.myData.push({ ...myDocuments, label: 'Moje dokumenty' });
          }
        }
        
        // Uporządkuj kolejność: Moje konto, Zgody, Komunikaty, Export danych, Moje dokumenty, Regulamin, FAQ
        newRoutes.myData = [
          newRoutes.myData.find((item: IRoute) => item.key === 'my-account') || newRoutes.myData[0],
          newRoutes.myData.find((item: IRoute) => item.key === 'consent'),
          newRoutes.myData.find((item: IRoute) => item.key === 'message-settings'),
          newRoutes.myData.find((item: IRoute) => item.key === 'export'),
          newRoutes.myData.find((item: IRoute) => item.key === 'documents'),
          newRoutes.myData.find((item: IRoute) => item.key === 'terms'),
          newRoutes.myData.find((item: IRoute) => item.key === 'faq')
        ].filter(Boolean);
      }
    }

    return newRoutes;
  };

  const result: any = {};
  Object.keys(coreRoutes).forEach((role) => {
    result[role] = reorganizeRoutes(coreRoutes[role], role);
  });

  return result;
};

const SubLayoutDashboard: FC<IProps> = ({ urlPrefix }) => {
  const navigate = useAppNavigate();
  const { pathname } = useLocation();

  // dane użytkownika - używamy state.auth.profile jak w core
  const { profile } = useSelector((state) => state.auth);

  // czy jesteśmy w panelu administracyjnym?
  const isAdminPanel = pathname.includes('/managment_panel');

  // dostępne ścieżki dla użytkownika
  const accessRoutes = userRolesAccessRoutes(isAdminPanel)[profile?.role || 'ROLE_USER_MAIN'] || {};

  // funkcja testująca czy podana ścieżka jest aktualną stroną
  const isCurrentPath = useCallback(
    (testedPath?: string) =>
      testedPath ? new RegExp(`^/[^\\/]+${testedPath}$`).test(pathname) : false,
    [pathname]
  );

  // ustawienie breadcrumbs'ów po zamontowaniu komponentu
  useEffect(() => {
    each(accessRoutes, (routes) => {
      const currentRoute = find(routes, (route) => isCurrentPath(route.path));

      if (currentRoute) {
        navigate(currentRoute.path, { replace: true });
      }
    });
  }, []);

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Components-Layouts-DashboardSubLayout')}>
      <Container>
        <Breadcrumbs />
        <Grid container columnSpacing="32px">
          <Grid item md={2} xs={12} className={styles.menuWrapper}>
            {profile?.role && (
              <DashboardMenu isAdminPanel={isAdminPanel} accessRoutes={accessRoutes} />
            )}
          </Grid>
          <Grid item md={10} xs={12}>
            <Outlet />
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default SubLayoutDashboard;
